import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, clearTokens, login as authLogin } from '../utils/auth.js'
import { invalidateSavedListsCache } from '../savedListsCache.js'
import { COOK_MODE_RESUME_KEY } from '../utils/cookModeResume.js'
import { writeUserAccountSnapshot } from '../utils/accountSnapshot.js'
import { markHasAccount } from '../utils/guestKitchen.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [canViewAdmin, setCanViewAdmin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState(null)

  const clearAuth = useCallback(({ deliberate = false } = {}) => {
    clearTokens()
    setIsAuthenticated(false)
    setIsAdmin(false)
    setCanViewAdmin(false)
    setUser(null)
    
    // Clear user-specific data from localStorage
    localStorage.removeItem('grocery_list')
    localStorage.removeItem('selected_store')
    // Drop remembered tab so the next sign-in defaults to Plan (unless ?tab= deep link).
    localStorage.removeItem('agent_active_tab')
    localStorage.removeItem(COOK_MODE_RESUME_KEY)
    // Keep the account snapshot on logout so Home can greet a known account
    // holder (H-R) instead of the brand-new visitor page.

    // Invalidate saved lists cache
    invalidateSavedListsCache()
    
    // Dispatch logout event to notify components to reset their state
    window.dispatchEvent(new CustomEvent('userLoggedOut', { detail: { deliberate } }))
  }, [])

  const checkAuth = useCallback(async () => {
    try {
      // Same flow in dev and production: require a valid token to be authenticated.
      // If access token is missing but a refresh token exists, attempt refresh first
      // so we don't strand users in a "logged in" UI state with no Authorization header.
      const token = getAccessToken()
      if (!token) {
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          try {
            const refreshResponse = await fetch('/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken })
            })

            if (refreshResponse.ok) {
              const tokenData = await refreshResponse.json()
              setAccessToken(tokenData.access_token)
              setRefreshToken(tokenData.refresh_token)
              // Continue below to /auth/me using the refreshed access token
            } else {
              clearAuth()
              setIsLoading(false)
              return
            }
          } catch {
            clearAuth()
            setIsLoading(false)
            return
          }
        } else {
          setIsAuthenticated(false)
          setIsAdmin(false)
          setCanViewAdmin(false)
          setUser(null)
          setIsLoading(false)
          return
        }
      }

      const finalToken = getAccessToken()
      if (!finalToken) {
        clearAuth()
        setIsLoading(false)
        return
      }

      // Verify token by checking user info
      const response = await fetch('/auth/me', {
        headers: {
          'Authorization': `Bearer ${finalToken}`
        }
      })

      if (response.ok) {
        const userData = await response.json()
        setIsAuthenticated(true)
        setIsAdmin(userData.is_admin || false)
        setCanViewAdmin(userData.can_view_admin || userData.is_admin || false)
        setUser(userData)
        markHasAccount()
        writeUserAccountSnapshot(userData)
      } else if (response.status === 401) {
        // Token is invalid or expired, try to refresh
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          try {
            const refreshResponse = await fetch('/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken })
            })

            if (refreshResponse.ok) {
              const tokenData = await refreshResponse.json()
              setAccessToken(tokenData.access_token)
              setRefreshToken(tokenData.refresh_token)
              
              // Get user info again
              const userResponse = await fetch('/auth/me', {
                headers: {
                  'Authorization': `Bearer ${tokenData.access_token}`
                }
              })
              
              if (userResponse.ok) {
                const userData = await userResponse.json()
                setIsAuthenticated(true)
                setIsAdmin(userData.is_admin || false)
                setCanViewAdmin(userData.can_view_admin || userData.is_admin || false)
                setUser(userData)
                markHasAccount()
                writeUserAccountSnapshot(userData)
              } else {
                // Refresh succeeded but user info still fails - clear auth
                clearAuth()
              }
            } else {
              // Refresh failed - token is invalid, clear auth silently
              clearAuth()
            }
          } catch {
            // Network error during refresh - clear auth silently
            clearAuth()
          }
        } else {
          // No refresh token available - clear auth silently
          clearAuth()
        }
      } else {
        // Unexpected error status - clear auth
        clearAuth()
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      clearAuth()
    } finally {
      setIsLoading(false)
    }
  }, [clearAuth])

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Listen for token refresh failures and clear auth
  useEffect(() => {
    const handleTokenRefreshFailed = () => {
      console.log('Token refresh failed, clearing auth state')
      clearAuth()
    }
    
    window.addEventListener('tokenRefreshFailed', handleTokenRefreshFailed)
    return () => {
      window.removeEventListener('tokenRefreshFailed', handleTokenRefreshFailed)
    }
  }, [clearAuth])

  const login = useCallback(async (email, password, rememberMe = true) => {
    try {
      const data = await authLogin(email, password, rememberMe)
      
      // Get user info
      const userResponse = await fetch('/auth/me', {
        headers: {
          'Authorization': `Bearer ${data.access_token}`
        }
      })

      if (userResponse.ok) {
        const userData = await userResponse.json()
        setIsAuthenticated(true)
        setIsAdmin(userData.is_admin || false)
        setCanViewAdmin(userData.can_view_admin || userData.is_admin || false)
        setUser(userData)
        markHasAccount()
        writeUserAccountSnapshot(userData)
        window.dispatchEvent(new CustomEvent('userLoggedIn'))
        return { success: true, user: userData }
      } else {
        // If we cannot validate the access token via /auth/me, treat login as failed.
        // This prevents "signed in" UI states with invalid/expired/missing tokens.
        clearTokens()
        setIsAuthenticated(false)
        setIsAdmin(false)
        setCanViewAdmin(false)
        setUser(null)
        throw new Error('Login succeeded but user validation failed. Please try logging in again.')
      }
    } catch (error) {
      console.error('Login failed:', error)
      // Clear tokens if login failed
      clearTokens()
      throw error
    }
  }, [])

  const logout = useCallback(() => {
    const refreshToken = getRefreshToken()
    const accessToken = getAccessToken()
    // Revoke server session in the background — do not await. If this fetch hangs
    // (slow proxy, TLS stall, etc.), awaiting would block clearAuth() and the UI
    // would never leave the signed-in state.
    if (refreshToken && accessToken) {
      void fetch('/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch((err) => {
        console.error('Logout API call failed:', err)
      })
    }
    clearAuth({ deliberate: true })
  }, [clearAuth])

  /** Revoke all server sessions for this user, then clear local tokens (sign out everywhere). */
  const logoutAllDevices = useCallback(() => {
    const accessToken = getAccessToken()
    if (accessToken) {
      void fetch('/auth/logout-all-devices', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).catch((err) => {
        console.error('Log out all devices API call failed:', err)
      })
    }
    clearAuth({ deliberate: true })
  }, [clearAuth])

  const value = useMemo(() => ({
    isAuthenticated,
    isAdmin,
    canViewAdmin,
    isLoading,
    user,
    login,
    logout,
    logoutAllDevices,
    checkAuth
  }), [isAuthenticated, isAdmin, canViewAdmin, isLoading, user, login, logout, logoutAllDevices, checkAuth])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

